import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { FollowsResponse } from "@skills-marketplace/contracts/contract-11-follows";
import { isFollowingEnabled } from "./feature-flags";
import { fetchFollows, followItem, unfollowItem } from "./follow-client";
import { reportEvent } from "./newrelic";
import { GitHubProxyError, getMyRecords } from "./github-proxy-client";
import FollowToast, { type FollowNotice } from "../components/FollowToast";

/** `loading` before the first GET settles; `error` when the GET failed (null response). */
export type FollowsStatus = "loading" | "ready" | "error";

interface FollowsContextValue {
  enabled: boolean;
  status: FollowsStatus;
  response: FollowsResponse | null;
  /** True when the user follows this installName directly (not only via a followed plugin). */
  isFollowing: (installName: string) => boolean;
  /** True for items the caller publishes; they never get a Follow control. */
  isOwn: (installName: string) => boolean;
  toggle: (installName: string, name: string, follow: boolean) => Promise<void>;
  refresh: () => Promise<FollowsResponse | null>;
}

const noop = async () => {};

const FollowsContext = createContext<FollowsContextValue>({
  enabled: false,
  status: "loading",
  response: null,
  isFollowing: () => false,
  isOwn: () => false,
  toggle: noop,
  refresh: async () => null,
});

function directNames(response: FollowsResponse | null): Set<string> {
  return new Set(response?.items.filter((item) => item.followedDirectly).map((item) => item.installName) ?? []);
}

/**
 * Single source for the caller's follows, so the catalog, detail pages and Following page
 * agree. Mounted under AuthGuard (the GET needs a token) and does nothing while
 * REACT_APP_FOLLOWING_ENABLED is off.
 */
export function FollowsProvider({ children }: { children: ReactNode }) {
  const enabled = isFollowingEnabled();
  const [status, setStatus] = useState<FollowsStatus>("loading");
  const [response, setResponse] = useState<FollowsResponse | null>(null);
  const [direct, setDirect] = useState<Set<string>>(() => new Set());
  const [own, setOwn] = useState<Set<string>>(() => new Set());
  const [notice, setNotice] = useState<FollowNotice | null>(null);
  const noticeId = useRef(0);
  // Writes in flight: installName → the membership the user just chose.
  const pending = useRef(new Map<string, boolean>());
  const refreshSeq = useRef(0);
  const appliedSeq = useRef(0);

  const refresh = useCallback(async () => {
    if (!enabled) return null;
    const seq = ++refreshSeq.current;
    const next = await fetchFollows();
    if (!next) {
      // Keep the last good list; a failed refresh must not blank it.
      setStatus((current) => (current === "ready" ? current : "error"));
      return null;
    }
    // An older GET that resolves after a newer one must not overwrite it, but its caller still gets the read.
    if (seq <= appliedSeq.current) return next;
    appliedSeq.current = seq;
    const names = directNames(next);
    for (const [installName, on] of pending.current) {
      if (on) names.add(installName);
      else names.delete(installName);
    }
    setResponse(next);
    setDirect(names);
    setStatus("ready");
    return next;
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!enabled) return;
    getMyRecords()
      .then((res) =>
        setOwn(new Set(res.owned.map((entry) => entry.installName).filter((name): name is string => !!name))),
      )
      .catch((err) => {
        console.warn("[follow] could not load own items", err);
        setOwn(new Set());
      });
  }, [enabled]);

  const isFollowing = useCallback((installName: string) => direct.has(installName), [direct]);
  const isOwn = useCallback((installName: string) => own.has(installName), [own]);

  const toggle = useCallback(
    async (installName: string, name: string, follow: boolean) => {
      if (pending.current.has(installName)) return;
      const id = ++noticeId.current;
      const setMembership = (on: boolean) =>
        setDirect((prev) => {
          const next = new Set(prev);
          if (on) next.add(installName);
          else next.delete(installName);
          return next;
        });

      pending.current.set(installName, follow);
      setMembership(follow);
      try {
        if (follow) await followItem(installName);
        else await unfollowItem(installName);
      } catch (err) {
        pending.current.delete(installName);
        setMembership(!follow);
        const limit = err instanceof GitHubProxyError && err.statusCode === 409;
        setNotice({
          id,
          tone: "error",
          message: limit
            ? err.message
            : `Couldn't ${follow ? "follow" : "unfollow"} ${name}. Try again.`,
        });
        return;
      }

      pending.current.delete(installName);
      reportEvent(follow ? "marketplace_follow" : "marketplace_unfollow", { installName });
      setNotice({ id, tone: "success", message: follow ? `Following ${name}` : `Unfollowed ${name}` });
      void refresh();
    },
    [refresh],
  );

  const value = useMemo<FollowsContextValue>(
    () => ({ enabled, status, response, isFollowing, isOwn, toggle, refresh }),
    [enabled, status, response, isFollowing, isOwn, toggle, refresh],
  );

  return (
    <FollowsContext.Provider value={value}>
      {children}
      {enabled && <FollowToast notice={notice} onDismiss={() => setNotice(null)} />}
    </FollowsContext.Provider>
  );
}

export function useFollows(): FollowsContextValue {
  return useContext(FollowsContext);
}
